import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';

import SubtleBadge from 'components/common/SubtleBadge';
import {
  getBulkActionTypeBadgeColor,
  getBulkActionStatusBadgeColor,
} from 'constants/bulkAction';
import { TIME_OPTION } from 'constants/time';

const FailReasonCell = ({ failureReason }) => {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!failureReason) return <span>-</span>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(failureReason);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <span
        className="d-block text-truncate text-primary cursor-pointer"
        onClick={() => setShow(true)}
      >
        {failureReason}
      </span>

      <Modal
        show={show}
        onHide={() => {
          setShow(false);
          setCopied(false);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Fail Reason</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '4px',
              margin: 0,
            }}
          >
            {failureReason}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={copied ? 'success' : 'falcon-default'}
            size="sm"
            onClick={handleCopy}
          >
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
          <Button
            variant="falcon-primary"
            size="sm"
            onClick={() => setShow(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export const BULK_ACTION_LIST_COLUMNS = [
  {
    accessorKey: 'type',
    header: 'Type',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { type } = original;

      return (
        <SubtleBadge pill bg={getBulkActionTypeBadgeColor(type)}>
          {type}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'user',
    header: 'Uploaded by',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { user } = original;

      return <span>{user?.email || '-'}</span>;
    },
  },
  {
    accessorKey: 'row_count',
    header: 'Row Count',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { row_count } = original;

      return <span>{row_count.toLocaleString()}</span>;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { status } = original;

      return (
        <SubtleBadge pill bg={getBulkActionStatusBadgeColor(status)}>
          {status}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { created } = original;

      return (
        <span>{new Date(created).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
  {
    accessorKey: 'modified',
    header: 'Modified',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { modified } = original;

      return (
        <span>{new Date(modified).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
  {
    accessorKey: 'failure_reason',
    header: 'Fail Reason',
    enableSorting: false,
    meta: {
      valueType: 'string',
      truncate: true,
      minWidth: '300px',
      maxWidth: '300px',
    },
    cell: ({ row: { original } }) => (
      <FailReasonCell failureReason={original.failure_reason} />
    ),
  },
];
